// 性能优化（FCP/LCP）：
// 之前 import { isApp, updateCheckIsPwa } from "@/utils/device-helpers"
// 会传递性把 utils.ts（2000+ 行 + axios + crypto-js + i18n …）打进 entry chunk，
// 让落地页/主应用 entry 评估多花 100-200ms（直接命中 LCP）。
// 现在改成只引 env-helpers / storage-helpers，体积大幅压缩。
//
// 同时 trackingService 改为事件触发时再动态 import，
// 避免 init 阶段就把整个埋点依赖图拉进来。
//
// PWA 安装关键链路（修复"点击安装无进度弹窗"）：
//   - beforeinstallprompt 监听器、appinstalled 监听器、window.deferredPromptPrompt
//     全部在「模块顶层」注册，而不是放在 initPwa() 函数体内。
//   - 模块被 entry (main.tsx / landing-main.tsx) 同步 import → 模块顶层代码同步执行
//     → 监听器 / 全局函数立刻可用，零等待。
//   - 之前放在 initPwa() 内，虽然 initPwa() 是 async 但没 await（函数体也是同步执行），
//     理论上等价，但实际上有一个边缘风险：若调用方写成
//     `(async () => { await initPwa(); })()` 这种 wrapper IIFE，函数体内 await
//     之前的代码也是同步的，但任何额外的 microtask 调度都可能让 listener 注册
//     落到浏览器派发 beforeinstallprompt 之后（特别是页面已满足 PWA 条件时
//     浏览器会非常早地派发该事件）。
//   - 模块顶层注册无任何 microtask 调度，最稳。
import { getObject, setObject } from "@/utils/storage-helpers";
import { isApp, updateCheckIsPwa } from "@/utils/env-helpers";

// PWA standalone 启动时种持久 cookie. 修复 StaticController.shouldShowPwaGuideInvite
// 在 URL 丢失 ?pwa=true query 后误跳 PwaGuide 引导页的 bug.
//   - PWA 安装后 manifest start_url=/?pwa=true => 启动 URL 带 query => AppUser.isPwa()=true
//   - 但 PWA 内下拉刷新 / React Router 导航后 URL 可能变成 / (query 丢失)
//     => AppUser.isPwa()=false => 命中 INVITE 条件 => 错误跳转 PwaGuide
//   - cookie 兜底: 一旦检测到 standalone 启动, 种 1 年 cookie, 后续请求自动带, 不依赖 URL.
try {
  if (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches
  ) {
    // 种 cookie 兜底 (1 年自动续期, 每次 PWA 启动跑一次)
    document.cookie = "is_pwa=1; max-age=31536000; path=/; SameSite=Lax";
    // 修正 URL 加回 ?pwa=true (history.replaceState 不引起 navigation, 仅改地址栏).
    // 用户下拉刷新时浏览器用修正后的 URL 发请求, 后端 AppUser.isPwa() 仍能识别.
    // 双保险: 即使 cookie 被清, URL query 也能命中.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("pwa") !== "true") {
        url.searchParams.set("pwa", "true");
        window.history.replaceState(null, "", url.toString());
      }
    } catch {
      // ignore URL 构造失败 (非常旧浏览器)
    }
  }
} catch {
  // ignore
}

// === 模块顶层 PWA 监听器：最早注册，避免错过浏览器派发的事件 ===

document.addEventListener("DOMContentLoaded", () => {
  const manifest = document.querySelector('link[rel="manifest"]');
  const fromFb = document.referrer.includes("facebook.com");
  const firstLoad = !window.location.search.includes("reloaded");

  if (
    (!manifest ||
      !(manifest as HTMLLinkElement).href.includes("manifest.webmanifest")) &&
    fromFb &&
    firstLoad
  ) {
    if (!window.location.search.includes("reloaded")) {
      const url = new URL(window.location.href);
      url.searchParams.set("reloaded", "1");
      window.location.href = url.toString();
    }
  }
});

window.addEventListener("beforeinstallprompt", e => {
  e.preventDefault();
  window.deferredPrompt = e;

  // trackingService 体积较大（依赖 axios 等），事件触发时再动态加载
  import("@/services/trackingService").then(({ default: ts }) =>
    ts.trackPwaInstallPrompt().catch(err => {
      console.error("上报PWA_INSTALL_PROMPT失败:", err);
    })
  );

  try {
    const hadMark = localStorage.getItem("pwa_ever_installed");
    if (hadMark === "true") {
      localStorage.removeItem("pwa_ever_installed");
      localStorage.removeItem("pwa_installed_time");
      window.dispatchEvent(new CustomEvent("PWA_STATUS_CHANGED"));
    }
  } catch (e) {
    console.error("清除PWA安装标记失败:", e);
  }
});

// appinstalled 永远走完整流程, 不再按 timeDiff < 4s 区分.
// 旧"快速安装"分支吞掉 PWA_APP_INSTALLED 导致 PC 上(安装 1-2s 极快场景)
// useProgressState 模拟上限 98% 且 PWA_PRECACHE_COMPLETE 死代码, 遮罩永久卡 98%.
// 现在统一派发 PWA_APP_INSTALLED, "防一闪而过"由 useProgressState 收到
// 事件后用 完成动画 (250-800ms 根据当前进度) + 100% 停留 300ms 来保证可视化反馈.
window.addEventListener("appinstalled", () => {
  try {
    localStorage.setItem("pwa_ever_installed", "true");
    localStorage.setItem("pwa_installed_time", Date.now().toString());
  } catch (e) {
    console.error("保存PWA安装标记失败:", e);
  }

  (window as any).__pwaInstallStartTime = null;
  window.deferredPrompt = null;
  import("@/services/trackingService").then(({ default: ts }) =>
    ts.trackPwaAppInstalled().catch(err => {
      console.error("上报PWA_APP_INSTALLED失败:", err);
    })
  );
  window.dispatchEvent(new CustomEvent("PWA_APP_INSTALLED"));
});

// 全局函数：用户点击落地页 "Install" 按钮时由 useDownload 调用，
// 真正触发浏览器原生 PWA 安装弹窗（截图 2 中的 "Quick Installation"）。
// 必须在用户能点击之前就挂上 window，所以放在模块顶层而不是 initPwa() 函数内。
window.deferredPromptPrompt = function () {
  if (!window.deferredPrompt) return;

  // 标记安装开始时间，用于 appinstalled 时区分快速/慢速安装路径
  (window as any).__pwaInstallStartTime = Date.now();

  window.deferredPrompt.prompt();
  window.deferredPrompt.userChoice.then((choiceResult: any) => {
    if (choiceResult.outcome === "accepted") {
      // useDownload 的 useEffect 监听该事件 → setIsPwaInstalling(true) +
      // setShowProgress(true)，触发 LandingPage 的进度遮罩
      window.dispatchEvent(new CustomEvent("PWA_INSTALL_ACCEPTED"));
    } else {
      (window as any).__pwaInstalling = false;
      window.dispatchEvent(new CustomEvent("PWA_INSTALL_REJECTED"));
      const times = Number(getObject("pwa-dismissed-times") || 0);
      setObject("pwa-dismissed-times", times + 1);
    }
  });
};

// initPwa 现在是个 no-op（兼容旧调用点）：所有副作用已经在模块顶层完成。
// 调用方可以继续写 `await initPwa()`，不需要改动。
export async function initPwa(): Promise<void> {
  // 副作用已在模块顶层执行
}

export async function loopCheckPwa() {
  if (isApp()) return;
  if (updateCheckIsPwa()) {
    return;
  }
}

export async function initWebPush() {
  if (!("serviceWorker" in navigator)) return;

  try {
    const existingReg = await navigator.serviceWorker.getRegistration();
    if (existingReg) {
      return existingReg;
    }

    const newReg = await navigator.serviceWorker.register("/sw.js");
    return newReg;
  } catch (err) {
    console.error("SW registration failed:", err);
  }
}
